import React, { useRef, useEffect, useState } from 'react';

interface PlaceResult {
  address: string;
  lat?: number;
  lng?: number;
  placeId?: string;
}

interface GooglePlacesAutocompleteProps {
  value: string;
  onChange: (result: PlaceResult) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  error?: boolean;
}

declare global {
  interface Window {
    google: any;
    initGooglePlaces: () => void;
  }
}

/**
 * Google Places Autocomplete Component
 * 
 * This component uses the new PlaceAutocompleteElement API when available,
 * falling back to the legacy Autocomplete API for backward compatibility.
 * 
 * The new PlaceAutocompleteElement is Google's recommended approach as of March 2025.
 * 
 * For migration information, see:
 * https://developers.google.com/maps/documentation/javascript/places-migration-overview
 */

export const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
  value,
  onChange,
  placeholder = "Enter address...",
  className = "",
  disabled = false,
  id,
  name,
  error = false
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const placeElementRef = useRef<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [useNewAPI, setUseNewAPI] = useState(false);

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    
    if (!apiKey) {
      console.warn('Google Maps API key not found. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file');
      setLoadError(true);
      return;
    }

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      // Check if the new PlaceAutocompleteElement is available
      if (window.google.maps.places.PlaceAutocompleteElement) {
        setUseNewAPI(true);
        initializeNewPlaceAutocomplete();
      } else {
        initializeAutocomplete();
      }
      setIsLoaded(true);
      return;
    }

    // Check if script is already loading
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      // Wait for it to load
      window.initGooglePlaces = () => {
        if (window.google.maps.places.PlaceAutocompleteElement) {
          setUseNewAPI(true);
          initializeNewPlaceAutocomplete();
        } else {
          initializeAutocomplete();
        }
        setIsLoaded(true);
      };
      return;
    }

    // Load Google Maps script with loading=async for optimal performance
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&callback=initGooglePlaces`;
    script.async = true;
    script.defer = true;
    
    window.initGooglePlaces = () => {
      if (window.google.maps.places.PlaceAutocompleteElement) {
        setUseNewAPI(true);
        initializeNewPlaceAutocomplete();
      } else {
        initializeAutocomplete();
      }
      setIsLoaded(true);
    };

    script.onerror = () => {
      console.error('Failed to load Google Maps API');
      setLoadError(true);
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  const initializeNewPlaceAutocomplete = () => {
    if (!inputRef.current || !window.google?.maps?.places?.PlaceAutocompleteElement) return;

    try {
      // Create the new PlaceAutocompleteElement
      const placeAutocomplete = new window.google.maps.places.PlaceAutocompleteElement({
        componentRestrictions: { country: 'nz' }, // Restrict to New Zealand
        types: ['address'],
        fields: ['formatted_address', 'geometry.location', 'place_id', 'address_components']
      });

      placeElementRef.current = placeAutocomplete;

      // Replace the input with the PlaceAutocompleteElement
      if (inputRef.current && inputRef.current.parentNode) {
        // Copy attributes from the original input
        placeAutocomplete.id = inputRef.current.id;
        placeAutocomplete.className = inputRef.current.className;
        placeAutocomplete.placeholder = placeholder;
        placeAutocomplete.disabled = disabled;
        
        // Set initial value
        placeAutocomplete.value = value;

        // Add event listener for place selection
        placeAutocomplete.addEventListener('gmp-placeselect', (event: any) => {
          const place = event.place;
          
          if (!place.geometry || !place.geometry.location) {
            onChange({
              address: placeAutocomplete.value || '',
            });
            return;
          }

          const result: PlaceResult = {
            address: place.formattedAddress || placeAutocomplete.value || '',
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
            placeId: place.id
          };

          onChange(result);
        });

        // Replace the input element
        inputRef.current.parentNode.replaceChild(placeAutocomplete, inputRef.current);
        inputRef.current = placeAutocomplete as any;
      }
    } catch (error) {
      console.warn('Failed to initialize new PlaceAutocompleteElement, falling back to legacy API:', error);
      setUseNewAPI(false);
      initializeAutocomplete();
    }
  };

  const initializeAutocomplete = () => {
    if (!inputRef.current || !window.google?.maps?.places) return;

    // Use the legacy Autocomplete (still supported for existing customers)
    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ['address'],
      componentRestrictions: { country: 'nz' }, // Restrict to New Zealand
      fields: ['formatted_address', 'geometry.location', 'place_id', 'address_components']
    });

    autocompleteRef.current = autocomplete;

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      
      if (!place.geometry || !place.geometry.location) {
        // User entered the name of a Place that was not suggested and pressed Enter
        onChange({
          address: inputRef.current?.value || '',
        });
        return;
      }

      const result: PlaceResult = {
        address: place.formatted_address || inputRef.current?.value || '',
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        placeId: place.place_id
      };

      onChange(result);
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Update the value immediately for typing experience
    // Note: For the new PlaceAutocompleteElement, this might not be called
    // as it handles its own input events
    if (!useNewAPI) {
      onChange({
        address: e.target.value
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent form submission when Enter is pressed in autocomplete
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  if (loadError) {
    // Fallback to regular input if Google Maps fails to load
    return (
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`${className} ${error ? 'input-error' : ''}`}
        disabled={disabled}
        id={id}
        name={name}
      />
    );
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={useNewAPI ? undefined : value} // Let PlaceAutocompleteElement manage its own value
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`${className} ${error ? 'input-error' : ''}`}
        disabled={disabled || !isLoaded}
        id={id}
        name={name}
        style={useNewAPI ? { display: 'none' } : undefined} // Hide if replaced by PlaceAutocompleteElement
      />
      {!isLoaded && !loadError && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="w-4 h-4 border-2 border-slate-300 border-t-primary-500 rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
};
